import React from "react";
import moment from 'moment';
import PropTypes from 'prop-types';

import DateTimePicker from '@components/common/date_time_picker/main';
import { StandardCheckboxGroup } from '@components/library/checkbox_group/index';
import { StandardCheckbox } from '@components/library/checkbox/index';
import { PrimaryButton, TextWarningButton } from '@components/library/button/index';
import { BFLoader } from '@components/common/loader/main';
import { removeFromAllCollections } from '@api/v4/private/brandfolders/removeFromAllCollections';
import { removeOptions } from '@helpers/sweet_alert_options';
import { pluralizeObject, Brandfolder } from '@helpers/show_page_helpers';
import renderModal from '../modals/renderModal';

import './styles/BulkCollectionModal.scss';

class BulkCollectionModal extends React.Component {
  state = {
    adminable: BF.fx.role() === 'admin' || BF.fx.role() === 'owner' || BFG.currentUser.su,
    allSelected: false,
    availabilitySet: false,
    availability_start_date: moment().format('MM/DD/YYYY'),
    availability_start_time: '12:01 am',
    availability_start_zone: moment.tz.guess(),
    availability_end_date: moment().add(7, 'days').format('MM/DD/YYYY'),
    availability_end_time: '11:59 pm',
    availability_end_zone: moment.tz.guess(),
    collectionSearchValue: '',
    disableSubmitButton: true,
    displayCollectionSearch: false,
    existingCollections: [],
    isLoading: true,
    isSubmitting: false,
    new_collection_name: '',
    newCollection: false,
    selectedCollections: []
  }

  componentDidMount() {
    this.getCollections();
  }

  componentWillUnmount() {
    this.props.closeModal();
  }

  getCollections() {
    $.ajax({
      type: "GET",
      url: `/api/v4/brandfolders/${Brandfolder.key}/collections?fields=parent_id&per=1000&queue_priority=high`,
      contentType: "application/json",
      context: this,
      headers: { Authorization: `Bearer ${BF_Token}` },
      success: (response) => {
        if (!response.data.length) {
          this.setState({ newCollection: true, existingCollections: [] });
        } else {
          let collectionData;
          if (BFG?.resource?.type === "collection") {
            // render only subcollections for the parent (current resource)
            collectionData = response.data.filter((collection) => (
              collection?.attributes?.parent_id === BFG.resource.key));
          } else {
            // render only parent (top-level) collections
            collectionData = response.data.filter((collection) => (
              collection?.attributes?.parent_id === null));
          }
          this.setState({
            existingCollections: collectionData,
            displayCollectionSearch: response.data.length >= 10
          });
        }
      },
      error: () => {
      },
      complete: () => {
        this.setState({ isLoading: false });
      }
    });
  }

  availability_start = () => {
    const { availability_start_date, availability_start_time } = this.state;
    let { availability_start_zone } = this.state;
    availability_start_zone = availability_start_zone || moment.tz.guess();

    return moment.tz(`${availability_start_date} ${availability_start_time}`, 'MM/DD/YYYY hh:mm a', availability_start_zone);
  }

  availability_end = () => {
    const { availability_end_date, availability_end_time } = this.state;
    let { availability_end_zone } = this.state;
    availability_end_zone = availability_end_zone || moment.tz.guess();

    return moment.tz(`${availability_end_date} ${availability_end_time}`, 'MM/DD/YYYY hh:mm a', availability_end_zone);
  }

  buildDataBody = () => {
    const { selectedAssetKeys } = this.props;
    const { availabilitySet, selectedCollections, new_collection_name } = this.state;
    const selectedCollectionKeys = selectedCollections.map((collection) => collection.id);

    return {
      data: {
        asset_keys: Array.from(selectedAssetKeys),
        brandfolder_key: Brandfolder.key,
        collection_keys: selectedCollectionKeys,
        new_collection_name,
        collection_asset_associations: {
          availability_start: (availabilitySet ? this.availability_start().format() : null),
          availability_end: (availabilitySet ? this.availability_end().format() : null)
        }
      }
    };
  }

  createCollectionAssociations = () => {
    this.setState({ isSubmitting: true });

    const url = '/api/v4/bulk_actions/assets/collection_associations?queue_priority=high';
    const { newCollection, selectedCollections, new_collection_name } = this.state;
    const assetQtyObject = pluralizeObject(this.props.selectedAssetKeys);
    const assetCopy = assetQtyObject.plural ? bfTranslate('assets') : bfTranslate('asset');
    const collectionsQtyObject = pluralizeObject(selectedCollections);
    const collectionCopy = collectionsQtyObject.plural ? bfTranslate('collections') : bfTranslate('collection');

    $.ajax({
      type: "POST",
      url,
      contentType: "application/json",
      data: JSON.stringify(this.buildDataBody()),
      context: this,
      headers: { Authorization: `Bearer ${BF_Token}` },
      success: (response) => {
        this.props.closeModal();
        const body = this.state.availabilitySet ? bfTranslate('Assets may not appear immediately due to selected publish/expiration date(s).') : '';
        if (newCollection) {
          BF.fx.dispatchWindowEvent('newCollectionAdded', null, { newCollection: response.data[0].attributes });
          Notify.create({
            title: `${assetQtyObject.qty} ${assetCopy} ${bfTranslate('added to')} ${new_collection_name}!`,
            body,
            type: 'success'
          });
        } else if (selectedCollections.length === 1) {
          Notify.create({
            title: `${assetQtyObject.qty} ${assetCopy} ${bfTranslate('added to')} ${selectedCollections[0].attributes.name}!`,
            body,
            type: 'success'
          });
        } else {
          Notify.create({
            title: `${assetQtyObject.qty} ${assetCopy} ${bfTranslate('added to')} ${collectionsQtyObject.qty} ${collectionCopy}!`,
            body,
            type: 'success'
          });
        }
      },
      error: (xhr) => {
        const errorText = xhr.responseText;
        if (errorText.indexOf('Insufficient Plan') > -1) {
          this.props.closeModal();
          Notify.create({
            title: bfTranslate('Please upgrade to add more collections.'),
            type: 'error'
          });
        } else {
          this.setState({ disableSubmitButton: false, isSubmitting: false });
          Notify.create({
            title: `${bfTranslate('An error occurred when adding')} ${assetQtyObject.qty} ${assetCopy} ${bfTranslate('to')} ${collectionsQtyObject.qty} ${collectionCopy}. ${bfTranslate('Please try again.')}`,
            type: 'error'
          });
        }
      }
    });
  }

  handleRemoveAll = () => {
    const { selectedAssetKeys } = this.props;
    const thingToRemove = Array.from(selectedAssetKeys).length === 1
      ? bfTranslate('1 asset from all Collections')
      : `${Array.from(selectedAssetKeys).length} ${bfTranslate('assets from all Collections')}`;
    const successNotif = Array.from(selectedAssetKeys).length === 1
      ? bfTranslate('1 asset has been removed from all Collections')
      : `${Array.from(selectedAssetKeys).length} ${bfTranslate('assets have been removed from all Collections')}`;
    const errorNotif = Array.from(selectedAssetKeys).length === 1
      ? bfTranslate('An error ocurred when removing 1 asset from all Collections')
      : bfTranslate('An error ocurred when removing assets from all Collections');

    window.swal(removeOptions({ thingToRemove }), async (confirm) => {
      if (confirm) {
        try {
          await removeFromAllCollections(BFG.brandfolder_key, Array.from(selectedAssetKeys));
          Notify.create({ title: successNotif, type: 'success' });
          this.props.closeModal();
        } catch (err) {
          Notify.create({ title: errorNotif, type: 'error' });
        } finally {
          this.props.closeModal();
        }
      }
    });
  }

  enableActionButton = () => {
    const { availabilitySet, selectedCollections, newCollection, new_collection_name } = this.state;
    let enabled = true;

    // validate publish and expiration dates
    if (availabilitySet) {
      if (this.availability_start().isValid() && this.availability_end().isValid()) {
        enabled = this.availability_start().isBefore(this.availability_end()); // is expire date after than publish date
      } else {
        enabled = false;
      }
    }

    // validate collection selections if dates are validated
    if (enabled) {
      if (selectedCollections.length || (newCollection === true && new_collection_name !== '')) {
        enabled = true;
      } else {
        enabled = false;
      }
    }

    this.setState({ disableSubmitButton: !enabled });
  }

  updateDateTimeStart = (availability_start_date, availability_start_time, availability_start_zone) => {
    this.setState({
      availability_start_date,
      availability_start_time,
      availability_start_zone
    }, () => this.enableActionButton());
  }

  updateDateTimeEnd = (availability_end_date, availability_end_time, availability_end_zone) => {
    this.setState({
      availability_end_date,
      availability_end_time,
      availability_end_zone
    }, () => this.enableActionButton());
  }

  updateSelectedCollections = (collectionId) => {
    this.setState((prevState) => {
      let { allSelected } = prevState;
      let selectedCollections = [...prevState.selectedCollections];
      const existingCollections = [...prevState.existingCollections];

      if (collectionId === 'all-collections') {
        allSelected = !allSelected;
        selectedCollections = allSelected ? [...existingCollections] : [];
      } else {
        const checkedIndex = selectedCollections.findIndex((selectedCollection) => selectedCollection.id === collectionId);
        const clickedCollection = [...existingCollections].find((collection) => collection.id === collectionId);

        if (checkedIndex > -1) {
          selectedCollections.splice(checkedIndex, 1);
        } else {
          selectedCollections.push(clickedCollection);
        }

        allSelected = selectedCollections.length === existingCollections.length;
      }

      return { selectedCollections, allSelected };
    }, () => this.enableActionButton());
  }

  toggleNewCollection() {
    if (this.state.newCollection) {
      this.setState((prevState) => ({
        newCollection: false,
        displayCollectionSearch: prevState.existingCollections.length >= 10,
        new_collection_name: '',
        allSelected: false
      }), () => this.enableActionButton());
    } else {
      this.setState({
        newCollection: true,
        displayCollectionSearch: false,
        selectedCollections: []
      }, () => this.enableActionButton());
    }
  }

  toggleExpireAssets() {
    if (this.state.availabilitySet) {
      this.setState({ availabilitySet: false }, () => this.enableActionButton());
    } else {
      this.setState({ availabilitySet: true }, () => this.enableActionButton());
    }
  }

  resourceReference() {
    const collectionContext = BFG?.resource?.type === "collection";
    return collectionContext ? "Subcollection" : "Collection";
  }

  sortCheckboxes(collections) {
    return collections.sort((a, b) => {
      if (a.attributes.name.toUpperCase() > b.attributes.name.toUpperCase()) {
        return 1;
      }
      if (b.attributes.name.toUpperCase() > a.attributes.name.toUpperCase()) {
        return -1;
      }
      return 0;
    });
  }

  checkboxifyCollections() {
    return this.sortCheckboxes([...this.state.existingCollections]).map((collection) => {
      const checkbox = this.state.selectedCollections.find((selectedCollection) => (
        selectedCollection.id === collection.id
      ));
      const checked = (typeof checkbox) !== 'undefined';
      return { id: collection.id, checked, labelCopy: collection.attributes.name };
    });
  }


  renderRowTitle() {
    const assetQtyObject = pluralizeObject(this.props.selectedAssetKeys);
    const { adminable, existingCollections } = this.state;

    if (!adminable && !existingCollections.length) { return null; }

    return (
      <>
        <span className="bold-text">
          {assetQtyObject.qty} {bfTranslate('asset')}{assetQtyObject.plural}
        </span>
        {existingCollections.length ? `${bfTranslate('to one or more')} ${this.resourceReference()}s` : bfTranslate('to a new Collection')}
      </>
    );
  }

  renderRadioButtons() {
    if (!this.state.adminable || !this.state.existingCollections.length || BFG?.resource?.type === "collection") {
      return null;
    }
    return (
      <div className="radio-button-container">
        <label className="radio-row">
          <h4>
            {this.state.existingCollections.length > 1
              ? bfTranslate('Add to existing Collections')
              : bfTranslate('Add to existing Collection')}
          </h4>
          <input
            className="radio-input"
            type="radio"
            name="radio"
            value="add-to-existing-collection"
            checked={!this.state.newCollection}
            onChange={() => this.toggleNewCollection()}
          />
          <span className="radio-dot" />
        </label>
        <label className="radio-row">
          <h4>{bfTranslate('Create new Collection')}</h4>
          <input
            className="radio-input"
            type="radio"
            name="radio"
            value="add-to-new-collection"
            checked={this.state.newCollection}
            onChange={() => this.toggleNewCollection()}
          />
          <span className="radio-dot" />
        </label>
      </div>
    );
  }

  renderCollectionSearch() {
    if (!this.state.displayCollectionSearch) { return null; }

    const hideCloseClass = this.state.collectionSearchValue === '' ? 'hide-close' : '';

    return (
      <div className="add-collection-search-container">
        <span className="icon bff-search" />
        <span
          className={`icon bff-close ${hideCloseClass}`}
          onClick={() => this.setState({ collectionSearchValue: '' })}
        />
        <input
          className="inputs add-collection-search"
          type="text"
          value={this.state.collectionSearchValue}
          onChange={(e) => { this.setState({ collectionSearchValue: e.target.value }); }}
          placeholder={bfTranslate("Search collections")}
        />
      </div>
    );
  }

  renderCollectionList() {
    if (!this.state.existingCollections.length || this.state.newCollection) { return null; }

    const filteredCollections = this.checkboxifyCollections().filter((checkbox) => (
      checkbox?.labelCopy?.toUpperCase()?.includes(this.state.collectionSearchValue?.toUpperCase())
    ));

    const checkboxes = [
      { id: 'all-collections', checked: this.state.allSelected, labelCopy: `${bfTranslate('All')} ${this.resourceReference()}s` },
      ...filteredCollections,
    ];

    const containerClass = this.state.displayCollectionSearch ? 'fixed-height' : '';
    return (
      <div className={`collection-list-container ${containerClass}`}>
        <StandardCheckboxGroup
          name="available-collections"
          onChange={this.updateSelectedCollections}
          checkboxes={checkboxes}
        />
      </div>
    );
  }

  renderNewCollectionInput() {
    const { adminable, newCollection } = this.state;
    if (!adminable || !newCollection) { return null; }

    return (
      <div className="collection-selector-container">
        <label className="brandfolder-label new-resource-label">
          {bfTranslate("Collection Name")}
          <input
            className="inputs"
            type="text"
            onChange={(e) => {
              this.setState({ new_collection_name: e.target.value }, () => this.enableActionButton());
            }}
          />
        </label>
      </div>
    );
  }

  renderNoAccess() {
    if (!this.state.adminable && !this.state.existingCollections.length) {
      return <h4 className="no-collections-copy">{bfTranslate('It looks like this Brandfolder has no collections. Contact an administrator to create a Collection.')}</h4>;
    }

    return null;
  }

  renderAvailabilitySelector() {
    if (!this.state.availabilitySet) { return null; }

    const {
      availability_start_date,
      availability_start_time,
      availability_start_zone,
      availability_end_date,
      availability_end_time,
      availability_end_zone } = this.state;

    return (
      <div className="expiration-container">
        <h4 className="row-title">{bfTranslate('Set Publish Date (hide from collection')}
          <span className="bold-text">{bfTranslate('until')}</span>
          {bfTranslate('this date)')}
        </h4>
        <DateTimePicker
          selectedDate={availability_start_date}
          selectedTime={availability_start_time}
          selectedZone={availability_start_zone}
          updateDateTime={this.updateDateTimeStart}
        />
        <div className="horiz-line" />
        <h4 className="row-title">{bfTranslate('Set Expiration Date (remove from collection')}
          <span className="bold-text">{bfTranslate('after')}</span>
          {bfTranslate('this date)')}
        </h4>
        <DateTimePicker
          selectedDate={availability_end_date}
          selectedTime={availability_end_time}
          selectedZone={availability_end_zone}
          updateDateTime={this.updateDateTimeEnd}
        />
      </div>
    );
  }

  render() {
    const {
      adminable,
      availabilitySet,
      disableSubmitButton,
      existingCollections,
      isLoading
    } = this.state;
    const { closeModal } = this.props;
    const loaderActive = isLoading ? 'loader-active' : '';

    return (
      <div className="modal-content-wrapper bulk-collection-modal">
        <div className="modal-content-wrapper__header">
          <span className="bff-plus icon" aria-hidden="true" />
          <h3 className="modal-title">{bfTranslate('Add')}
            {this.renderRowTitle()}
          </h3>
          <button
            className="close-button"
            onClick={closeModal}
            type="button"
          >
            <span className="bff-close" />
          </button>
        </div>
        <div className={`modal-content-wrapper__body ${loaderActive}`}>
          {isLoading
            ? <BFLoader />
            : (
              <div>
                <div className="modal-row">
                  { this.renderRadioButtons() }
                  { this.renderCollectionSearch() }
                  { this.renderCollectionList() }
                  { this.renderNewCollectionInput() }
                  { this.renderNoAccess() }
                </div>
                {(adminable || existingCollections.length !== 0) && (
                  <>
                    <div className="horiz-line" />
                    <div className="modal-row">
                      <div className="checkbox-container">
                        <StandardCheckbox
                          checked={availabilitySet}
                          labelCopy={`${bfTranslate('Set asset availability for')} ${this.resourceReference()}s`}
                          onChange={() => this.toggleExpireAssets()}
                        />
                      </div>
                      { this.renderAvailabilitySelector() }
                    </div>
                  </>
                )}
                <div className={`button-container ${BFG.resource.type === "brandfolder" ? "button-container__two-buttons" : ""}`}>
                  {BFG.resource.type === "brandfolder" && (
                    <TextWarningButton
                      icon="bff-trash"
                      onClick={() => this.handleRemoveAll()}
                    >
                      {bfTranslate("Remove from all Collections")}
                    </TextWarningButton>
                  )}
                  <PrimaryButton
                    className="t-confirm-modal"
                    disabled={disableSubmitButton}
                    isLoading={this.state.isSubmitting}
                    loadingCopy={bfTranslate('Submitting')}
                    onClick={() => this.createCollectionAssociations()}
                    size="small"
                    type="button"
                  >
                    {bfTranslate("Submit")}
                  </PrimaryButton>
                </div>
              </div>
            )
          }
        </div>
      </div>
    );
  }
}

BulkCollectionModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  selectedAssetKeys: PropTypes.instanceOf(Set).isRequired,
};

const ModalComponent = renderModal(BulkCollectionModal, 'BulkCollectionModal');
export default ModalComponent;
